import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'

const { FiMessageCircle, FiX, FiSend, FiLoader, FiBot, FiUser, FiMinus, FiHelpCircle } = FiIcons

const PromptlyHelpChatbot = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm your Promptly assistant. I can help you with using the prompt builder, understanding features, or any questions about creating perfect AI prompts. How can I help you today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ])
  const [inputMessage, setInputMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const sendMessage = async () => {
    if (!inputMessage.trim()) return

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setIsTyping(true)

    try {
      // Simulate AI response for Promptly help
      const response = await getPromptlyHelpResponse(inputMessage)
      
      const botMessage = {
        id: Date.now() + 1,
        text: response,
        sender: 'bot',
        timestamp: new Date()
      }

      setTimeout(() => {
        setMessages(prev => [...prev, botMessage])
        setIsTyping(false)
      }, 1000)
    } catch (error) {
      console.error('Error getting help response:', error)
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Sorry, I encountered an error. Please try asking your question again, or check out our documentation for help with using Promptly.',
        sender: 'bot',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
      setIsTyping(false)
    }
  }

  const getPromptlyHelpResponse = async (message) => {
    const lowerMessage = message.toLowerCase()

    // Promptly-specific help responses
    if (lowerMessage.includes('how') && (lowerMessage.includes('use') || lowerMessage.includes('work'))) {
      return "Great question! Here's how to use Promptly:\n\n1. **Choose a Category** - Select your project type (web development, mobile app, etc.)\n2. **Configure Settings** - Pick complexity, framework, and styling\n3. **Add Features** - Select the functionality you need\n4. **Generate Prompt** - Click the generate button to create your optimized prompt\n5. **Run with AI** - Test your prompt directly with OpenAI integration\n\nWould you like me to explain any of these steps in more detail?"
    }

    if (lowerMessage.includes('prompt') && lowerMessage.includes('builder')) {
      return "The Prompt Builder is Promptly's main feature! It helps you create optimized prompts for AI code generation. Here's what makes it special:\n\n🎯 **Smart Categories** - Pre-configured for different project types\n🤖 **AI Recommendations** - Get framework suggestions based on your needs\n💡 **Feature Selection** - Choose from hundreds of features automatically\n📋 **Templates** - Start with proven prompt templates\n\nWhat specific part of the prompt builder would you like to learn about?"
    }

    if (lowerMessage.includes('api') && lowerMessage.includes('key')) {
      return "To use the AI features, you'll need an OpenAI API key:\n\n1. **Go to Settings** (gear icon in the top navigation)\n2. **Sign in** to your account if you haven't already\n3. **Enter your OpenAI API key** - Get one from platform.openai.com\n4. **Test the key** to make sure it works\n\nYour API key is stored securely and encrypted. You can also use our Secure API mode which doesn't require your own key!"
    }

    if (lowerMessage.includes('categories') || lowerMessage.includes('template')) {
      return "Promptly offers 25+ categories with 85+ templates! Popular ones include:\n\n🌐 **Web Development** - React apps, landing pages, e-commerce\n📱 **Mobile Apps** - PWAs, task managers, social apps\n📊 **Data Analysis** - Dashboards, charts, analytics\n🎮 **Entertainment** - Games, media players, streaming\n💼 **Business** - CRM, booking systems, restaurants\n\nEach template includes optimized prompts, feature recommendations, and best practices. Browse them in the Categories tab!"
    }

    if (lowerMessage.includes('save') || lowerMessage.includes('saved')) {
      return "You can save your prompts for later use! Here's how:\n\n1. **Create your prompt** in the Prompt Builder\n2. **Click 'Save'** after generating your prompt\n3. **Access saved prompts** in the 'Saved Prompts' tab\n4. **Reload anytime** - Click the edit icon to load a saved prompt\n\nSaved prompts include all your settings, features, and custom requirements. Perfect for reusing successful prompt configurations!"
    }

    if (lowerMessage.includes('pwa') || lowerMessage.includes('progressive')) {
      return "PWA (Progressive Web App) features make your generated applications work like native apps:\n\n📱 **Installable** - Users can install your app on their devices\n🔄 **Offline Support** - Works without internet connection\n🔔 **Push Notifications** - Engage users with notifications\n⚡ **Fast Loading** - App shell architecture for speed\n🔄 **Background Sync** - Sync data when connection returns\n\nJust toggle 'Progressive Web App' in the prompt builder to include these features!"
    }

    if (lowerMessage.includes('admin') || lowerMessage.includes('console')) {
      return "The Admin Console feature adds comprehensive management capabilities to your generated applications:\n\n👥 **User Management** - Manage accounts and permissions\n📝 **Content Management** - Edit all website content\n📊 **Analytics** - Track performance and engagement\n🎨 **Custom Branding** - Upload logos and customize styling\n🏢 **Business Features** - Manage hours, bookings, menus (for applicable project types)\n\nPerfect for client projects or businesses that need full control over their applications!"
    }

    if (lowerMessage.includes('cost') || lowerMessage.includes('price') || lowerMessage.includes('credit')) {
      return "Promptly helps you save on AI costs through optimized prompts! Here's the breakdown:\n\n💰 **Cost Savings** - Optimized prompts use fewer tokens\n📊 **Usage Tracking** - See exactly how many tokens you're using\n💡 **Efficiency** - Get better results with fewer API calls\n🔒 **Secure Mode** - Use our API without providing your own key\n\nTypical costs: ~$0.001-$0.01 per prompt generation with GPT-4o-mini. Much cheaper than trial-and-error prompting!"
    }

    if (lowerMessage.includes('features') || lowerMessage.includes('functionality')) {
      return "Promptly automatically suggests features based on your project type! Examples:\n\n🌐 **Web Apps** - Authentication, database, responsive design\n📱 **Mobile** - Touch gestures, offline support, PWA features\n📊 **Dashboards** - Charts, real-time data, export functionality\n🛒 **E-commerce** - Shopping cart, payments, inventory\n🎮 **Games** - Scoring, levels, multiplayer support\n\nThe smart feature selection ensures you don't miss important functionality for your project type!"
    }

    if (lowerMessage.includes('help') || lowerMessage.includes('support') || lowerMessage.includes('stuck')) {
      return "I'm here to help! Here are some quick resources:\n\n❓ **Getting Started** - Try the Categories tab for templates\n🔧 **Technical Issues** - Check Settings for API key setup\n💡 **Best Practices** - Use Smart Recommendations for optimal results\n📖 **Documentation** - Each feature has helpful descriptions\n🤖 **AI Integration** - Test prompts with the 'Run with AI' feature\n\nWhat specific challenge are you facing? I can provide more targeted help!"
    }

    // Default helpful responses
    const defaultResponses = [
      "That's a great question about Promptly! Let me help you with that. Could you be more specific about what you'd like to know? I can help with:\n\n• How to use the prompt builder\n• Setting up API keys\n• Understanding features and templates\n• Saving and managing prompts\n• PWA and admin features\n• Cost optimization tips",
      
      "I'm here to help you get the most out of Promptly! Whether you need help with prompt building, API setup, or understanding features, just ask. What would you like to know more about?",
      
      `I see you're asking about "${message.split(' ').slice(0, 3).join(' ')}". I can help with all aspects of using Promptly! Try asking about:\n\n• Prompt building process\n• API configuration\n• Feature selection\n• Templates and categories\n• Saving prompts\n• AI integration`
    ]

    return defaultResponses[Math.floor(Math.random() * defaultResponses.length)]
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            className={`mb-4 bg-white rounded-2xl shadow-2xl border border-slate-200 ${
              isMinimized ? 'w-80 h-16' : 'w-80 h-96'
            } transition-all duration-300`}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-4 border-b border-slate-200 rounded-t-2xl cursor-pointer bg-gradient-to-r from-red-500 to-orange-500"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Promptly Assistant</h3>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-white/80 text-xs">Ready to help</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsMinimized(!isMinimized)
                  }}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiMinus} className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsOpen(false)
                  }}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Chat Messages */}
            {!isMinimized && (
              <>
                <div className="flex-1 p-4 h-64 overflow-y-auto">
                  <div className="space-y-3">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.sender === 'user' ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-xs px-3 py-2 rounded-lg ${
                            message.sender === 'user'
                              ? 'bg-red-600 text-white'
                              : 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-line">{message.text}</p>
                          <p
                            className={`text-xs mt-1 ${
                              message.sender === 'user'
                                ? 'text-red-100'
                                : 'text-slate-500'
                            }`}
                          >
                            {message.timestamp.toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                    {isTyping && (
                      <div className="flex justify-start">
                        <div className="bg-slate-100 text-slate-800 px-3 py-2 rounded-lg">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Promptly Assistant is typing...</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-slate-200">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask about using Promptly..."
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!inputMessage.trim() || isTyping}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <SafeIcon icon={FiSend} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Toggle Button */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 bg-gradient-to-r from-red-500 to-orange-500 rounded-full shadow-lg flex items-center justify-center text-white relative hover:shadow-xl transition-shadow"
        >
          <SafeIcon icon={FiHelpCircle} className="w-6 h-6" />
          
          {/* Help indicator */}
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
            <span className="text-xs text-white font-bold">?</span>
          </div>
        </motion.button>
      )}
    </div>
  )
}

export default PromptlyHelpChatbot